import { useTranslation } from "react-i18next";
import { InspectionModalSettings } from "../types";
import Heading from "@/components/atoms/Heading";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { SummaryStrip } from "../components/FormParts";
import { InspectionFormApi, InspectionSummary } from "../useInspectionForm";
import { useInspectionFormat } from "../useInspectionFormat";
import { DeviationPreview } from "../useDeviationPreview";
import { isPerOrder, toNumber } from "../useInspectionForm";
import styles from "../styles.module.css";

interface ReviewStepProps {
  form: InspectionFormApi;
  summary: InspectionSummary;
  settings: InspectionModalSettings;
  preview: DeviationPreview;
}

/** The last step, "Review": what will be registered, and where a deviation goes next. */
export default function ReviewStep({ form, summary, settings, preview }: ReviewStepProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();
  const { values } = form;

  return (
    <div className={styles.stepBody}>
      <SummaryStrip
        items={[
          { label: t("inspections:summary.approvedWeight"), value: `${format.number(summary.totalWeightKg)} kg` },
          {
            label: t("inspections:summary.registrationNumber"),
            value: values.transportRegistrationNumber || "—",
          },
          { label: t("inspections:summary.poNumber"), value: values.poNumber || "—" },
        ]}
      />

      {values.multiFraction && (
        <div className={styles.fieldStack}>
          <Heading order={3} styleOrder={6} strong>
            {t("inspections:review.fractions")}
          </Heading>
          <div>
            {values.streams.map((stream) => (
              <div key={stream.key} className={styles.simpleList}>
                <Text size="sm">{summary.streamLabel(stream.key)}</Text>
                <Text size="sm">{format.number(toNumber(stream.weightKg))} kg</Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary.withDeviation ? (
        <div className={`${styles.callout} ${styles.calloutWarning}`} data-testid="inspection-review-deviation">
          <div className={styles.calloutTitle}>
            <SVGAsset name="warning-triangle" width={18} height={18} alt="" />
            <Text size="sm" strong>
              {preview.costs
                ? t("inspections:review.deviationTitle", {
                    amount: format.amount(preview.costs.total, preview.costs.currency),
                  })
                : t("inspections:review.deviationTitleWithoutAmount")}
            </Text>
          </div>
          <div className={styles.calloutBody}>
            {summary.productLines.map((line, index) => (
              <div key={`product-${index}`} className={styles.calloutLine}>
                <Text size="sm">{line.product.name}</Text>
                <Text size="sm" nowrap>
                  {format.number(line.weightKg)} kg
                </Text>
              </div>
            ))}
            {summary.serviceLines.map((line, index) => (
              <div key={`service-${index}`} className={styles.calloutLine}>
                <Text size="sm">{line.type.name}</Text>
                <Text size="sm" nowrap>
                  {isPerOrder(line.type)
                    ? t("inspections:deviations.perOrder")
                    : `${format.number(line.registeredNumber)} ${format.deviationUnit(line.type.unitCode)}`}
                </Text>
              </div>
            ))}
            <Text size="sm" mt={4}>
              {settings.isDeviationConfirmationEnabled
                ? t("inspections:review.sentForApproval")
                : t("inspections:review.addedToInvoiceBasis")}
            </Text>
          </div>
        </div>
      ) : (
        <div className={`${styles.callout} ${styles.calloutSuccess}`} data-testid="inspection-review-ok">
          <div className={styles.calloutTitle}>
            <SVGAsset name="checkmark-green-noBg" width={18} height={18} alt="" />
            <Text size="sm">{t("inspections:review.noDeviation")}</Text>
          </div>
        </div>
      )}
    </div>
  );
}
